import React, { Component, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import history from "../../history";
import { loginUser } from "../../redux/actions";
import { roleBasedUrl } from "../../helpers";
import * as yup from "yup";
import { Segment } from "semantic-ui-react";
import { requestForNewPassword, resetPassword } from "../../api/userApi";
import swal from "sweetalert";

export const ForgotPasswordInline = ({ onBack, redirectUrl }) => {
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [useGoogle, setUseGoogle] = useState(false);
    const [error, setError] = useState(null);
    const schema = yup.object().shape({
        email: yup.string().email("Enter a valid email").required("Email is required"),
    });
    return (
        <>
            <h2 className="login-title">Reset password</h2>
            <p className="login-subtitle">Enter your email and we&apos;ll send you a link to reset your password</p>
            {sent && useGoogle ? (
                <div className="forgot-password-success">
                    <div className="forgot-password-success-icon">
                        <i className="fab fa-google" />
                    </div>
                    <p className="login-subtitle mb-2">This account uses Google sign-in</p>
                    <p className="text-muted small mb-3">
                        Please use Sign in with Google instead. No password needed.
                    </p>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>
                        Back to sign in
                    </button>
                </div>
            ) : sent ? (
                <div className="forgot-password-success">
                    <div className="forgot-password-success-icon">
                        <i className="fas fa-envelope" />
                    </div>
                    <p className="login-subtitle mb-2">Check your email</p>
                    <p className="text-muted small mb-3">
                        If an account exists with that email, you will receive a password reset link shortly.
                    </p>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onBack}>
                        Back to sign in
                    </button>
                </div>
            ) : (
                <Formik
                    initialValues={{ email: "" }}
                    validationSchema={schema}
                    onSubmit={(values, { setSubmitting }) => {
                        setLoading(true);
                        setError(null);
                        requestForNewPassword(values)
                            .then((response) => {
                                setLoading(false);
                                setSubmitting(false);
                                setSent(true);
                                setUseGoogle(!!(response?.data?.use_google));
                            })
                            .catch((err) => {
                                setLoading(false);
                                setSubmitting(false);
                                setError(err?.response?.data?.errors?.join?.(" ") || "Something went wrong. Please try again.");
                            });
                    }}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                        <form onSubmit={handleSubmit} className="form-wrap mt-3">
                            {error && (
                                <div className="alert alert-danger mb-3" role="alert">
                                    {error}
                                </div>
                            )}
                            <div className="field">
                                <label>Email</label>
                                <Field
                                    type="email"
                                    name="email"
                                    className="form-control"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.email}
                                    placeholder="Enter your email"
                                />
                                <ErrorMessage name="email" />
                            </div>
                            <button
                                className="btn btn-secondary my-2 w-100"
                                type="submit"
                                disabled={isSubmitting || loading}
                            >
                                {loading ? "Sending…" : "Send reset link"}
                            </button>
                            <p className="login-create-account-footer mt-3 mb-0">
                                <button type="button" className="btn-link p-0 border-0 bg-transparent" onClick={onBack}>
                                    ← Back to sign in
                                </button>
                            </p>
                        </form>
                    )}
                </Formik>
            )}
        </>
    );
};

export const EmailPrompt = (props) => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const emailPromptSchema = yup.object().shape({
        email: yup.string().email().required("Required"),
    });
    return (
        <Segment loading={loading}>
            <Formik
                initialValues={{ email: "" }}
                validationSchema={emailPromptSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    setLoading(true);
                    requestForNewPassword(values)
                        .then((response) => {
                            setLoading(false);
                            setMessage((response.data || {}).message);
                            swal({
                                title: "Response",
                                text: (response.data || {}).message,
                                icon: response.status == 200 ? "success" : "error",
                            }).then(() => history.push("/"));
                        })
                        .catch(() => setLoading(false));
                }}
            >
                {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                    <div className="p-2">
                        <h4>Please Provide your email address</h4>
                        <form onSubmit={handleSubmit} className="form-wrap">
                            <div className="fields">
                                <div className="field">
                                    <label>Email</label>
                                    <Field type="text" name="email" className="form-control" onBlur={handleBlur} onChange={handleChange} value={values.email} placeholder="Email" />
                                    <ErrorMessage name="email" />
                                </div>
                            </div>
                            <div className="text-center">
                                <button className="btn btn-secondary my-2" type="submit" disabled={isSubmitting}>Submit</button>
                            </div>
                        </form>
                    </div>
                )}
            </Formik>
            {message && <div className="ui message">{message}</div>}
        </Segment>
    );
};

export const ChangePassword = (props) => {
    const [loading, setLoading] = useState(false);
    const changePasswordSchema = yup.object().shape({
        password: yup.string().required("Required"),
        confirm_password: yup
            .string()
            .oneOf([yup.ref("password"), null], "Passwords don't match!"),
    });
    const hash = (props.location && props.location.hash) || "";
    const query = hash.replace(/^#change_password\??/, "").replace(/^\?/, "");
    const token = new URLSearchParams(query).get("reset_token");
    if (!token) {
        return (
            <Segment placeholder className="text-center">
                <div className="ui icon header">
                    <i className="exclamation circle red huge icon" />
                    Invalid token
                </div>
            </Segment>
        );
    }
    return (
        <Segment loading={loading}>
            <Formik
                initialValues={{
                    password: "",
                    confirm_password: "",
                }}
                validationSchema={changePasswordSchema}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setLoading(true);
                    values.reset_token = token;
                    resetPassword(values)
                        .then((response) => {
                            setLoading(false);
                            if (response.data && response.data.jwt && response.data.user) {
                                props.loginUser(response.data.user);
                            }
                            swal({
                                title: "Password changed successfully",
                                text: response.data.message || "You are now logged in.",
                                icon:
                                    response.status == 200
                                        ? "success"
                                        : "error",
                            }).then(() => {
                                const path =
                                    response.data && response.data.user && response.data.user.role
                                        ? roleBasedUrl(response.data.user.role, "/")
                                        : "/";
                                history.push(path);
                            });
                        })
                        .catch((v) => {
                            setLoading(false);
                            console.log(v);
                        });
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                    /* and other goodies */
                }) => (
                    <div className="login-form card p-2">
                        <div className="card-body">
                            <div className="text-small">
                                Please enter new password
                            </div>
                            <form onSubmit={handleSubmit} className="form-wrap">
                                <div className="fields">
                                    <div className="field">
                                        <label>New Password</label>

                                        <Field
                                            type="password"
                                            name="password"
                                            className="form-control"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.password}
                                            placeholder="New Password"
                                        />
                                        <ErrorMessage name="password" />
                                    </div>
                                    <div className="field">
                                        <label>Confirm Password</label>

                                        <Field
                                            type="password"
                                            name="confirm_password"
                                            className="form-control"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.confirm_password}
                                            placeholder="Confirm Password"
                                        />
                                        <ErrorMessage name="confirm_password" />
                                    </div>
                                </div>
                                <button
                                    className="btn btn-secondary my-2 w-100"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    Submit
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </Formik>
        </Segment>
    );
};
